/**
 * Kalib AI — Sign-in (returning-user access screen).
 *
 * Spec rules (kalib-returning-user-signin-reveal-access-emergent-prompt.md):
 *   Headline: "Sign in"
 *   Optional helper: "Continue to your Account Reveals." (only if screen feels too bare)
 *   Fields: Email, Password, Remember me checkbox, Forgot password link.
 *   Buttons: Continue (CTA), Forgot password.
 *   No welcome-back marketing. No confidentiality copy. No product education.
 *
 * Routing rules:
 *   - If a user is already signed in (e.g. Remember me from a prior session),
 *     bypass this screen entirely — auto-redirect into the standard
 *     authenticated routing path (/intelligence for setup-complete users,
 *     /verify-email for unverified, /intake for incomplete).
 *   - On submit success, route based on user state. The
 *     EmailVerificationGate in App.js takes care of the unverified case;
 *     this component handles setup-complete vs. incomplete.
 */
import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BetaBadge } from "@/components/BetaBadge";
import { KalibLogo } from "@/components/KalibLogo";

function routeForUser(user, fallback = "/intake") {
  // Mirrors the backend account-state routing logic from the spec.
  if (!user) return fallback;
  if (!user.email_verified) return "/verify-email";
  if (user.intake_stage >= 2) return "/intelligence";
  return "/intake";
}

export default function SignInPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(() => {
    try { return localStorage.getItem("kalib_remember_me") !== "0"; } catch { return true; }
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") || "";
  const signUpHref = next ? `/signup?next=${encodeURIComponent(next)}` : "/signup";

  // Spec rule: "If 'Remember me' was selected on prior sign-in, the user may
  // bypass the sign-in screen entirely and land directly in the account-state
  // routing." Implemented as an auto-redirect when an authed user lands here.
  useEffect(() => {
    if (user && user.email) {
      navigate(next || routeForUser(user), { replace: true });
    }
    // user === null → loaded but not signed in (show form)
    // user === undefined → still loading auth
  }, [user, navigate, next]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      try { localStorage.setItem("kalib_remember_me", remember ? "1" : "0"); } catch { /* noop */ }
      const data = await login(email, password, remember);
      // Honour ?next= override; otherwise use spec's account-state routing.
      navigate(next || routeForUser(data), { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Don't flash the form to a still-loading session.
  if (user && user.email) return null;

  return (
    <div className="min-h-screen bg-zinc-100 flex flex-col" data-testid="signin-page">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="block"
              data-testid="signin-logo-link"
              aria-label="Kalib AI home"
            >
              <KalibLogo variant="light" height={28} testId="signin-header-logo" />
            </Link>
            <BetaBadge />
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          {/* Spec headline + optional minimal helper. No welcome-back copy.
              No confidentiality sermon. No product education. */}
          <h1
            className="text-2xl sm:text-3xl font-medium tracking-tight text-zinc-950 font-heading mb-1.5"
            data-testid="signin-headline"
          >
            Sign in
          </h1>
          <p className="text-[13px] text-zinc-700 mb-6" data-testid="signin-helper">
            Continue to your Account Reveals.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="signin-form">
            {error && (
              <div
                className="text-sm text-red-600 bg-red-50 border border-red-200 px-4 py-3"
                data-testid="signin-error"
              >
                {error}
              </div>
            )}
            <div>
              <label className="text-xs font-medium uppercase tracking-widest text-zinc-900 mb-1.5 block">Email</label>
              <Input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                autoComplete="email"
                className="rounded-none border-zinc-200 focus:ring-amber-700 focus:border-amber-700"
                data-testid="signin-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium uppercase tracking-widest text-zinc-900">Password</label>
                <a
                  href="mailto:hello@kalib.ai?subject=Forgot%20password&body=Please%20reset%20the%20password%20for%20my%20Kalib%20AI%20account%3A%20"
                  className="text-[11px] text-amber-700 hover:text-amber-800 font-medium"
                  data-testid="signin-forgot-password-link"
                >
                  Forgot password?
                </a>
              </div>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="rounded-none border-zinc-200 focus:ring-amber-700 focus:border-amber-700 pr-10"
                  data-testid="signin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-7 h-7 text-zinc-500 hover:text-amber-700 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                  data-testid="signin-password-toggle"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={1.75} /> : <Eye className="w-4 h-4" strokeWidth={1.75} />}
                </button>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="signin-remember-me-label">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 border border-zinc-300 text-amber-700 focus:ring-amber-700 focus:ring-offset-0 rounded-none cursor-pointer accent-amber-700"
                data-testid="signin-remember-me-checkbox"
              />
              <span className="text-[13px] text-zinc-900">Remember me on this device</span>
            </label>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-700 text-white hover:bg-amber-800 rounded-none text-sm font-medium h-10"
              data-testid="signin-submit-btn"
            >
              {loading ? "Signing in..." : "Continue"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-zinc-700 text-center">
            Don&rsquo;t have an account?{" "}
            <Link
              to={signUpHref}
              className="text-amber-700 hover:text-amber-800 font-medium"
              data-testid="signin-signup-link"
            >
              Get started
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
